import { clearLetter, placeLetter, spellAnswer, startSpell } from "@aybuben/client/lesson/spell.ts";
import { tileLetter, type SpellAnswer } from "@aybuben/core/exercises/spell/logic.ts";
import { faceText } from "@aybuben/core/face.ts";
import type { SpellStep } from "@aybuben/core/lesson.ts";
import { checkTyped, isTyped, typedAnswer } from "@aybuben/core/writing/check.ts";
import { useEffect, useState } from "react";

import { useCourse } from "../../content/context.tsx";
import { haptic } from "../../haptics.ts";
import { useStrings } from "../../i18n/index.tsx";
import { COL, FOCUS } from "../../theme.ts";
import { G } from "../../ui/primitives.tsx";
import { SoundButton, useSound } from "../../ui/sound.tsx";
import { useWriting } from "../../ui/writing/context.tsx";
import { TypedDiff } from "../../ui/writing/TypedDiff.tsx";
import { TypedInput } from "../../ui/writing/TypedInput.tsx";
import { WriteSwitch } from "../../ui/writing/WriteSwitch.tsx";
import { StepHead } from "../kit.tsx";
import { registerView, type ExerciseViewProps, type FeedbackProps } from "../view-registry.ts";

export function SpellView(props: ExerciseViewProps<SpellStep, SpellAnswer>) {
	const { step, setAnswer, status, retry, onSubmit } = props;
	const t = useStrings();
	const w = useWriting();
	return (
		<div>
			<StepHead title={t("lesson.spellTitle")} retry={retry} retryNote={t("lesson.retryNote")} />
			<div className="mt-2">
				<WriteSwitch disabled={!!status} onSwitch={() => setAnswer(null)} />
			</div>
			<p className="mt-4 text-2xl font-bold">{step.word.m}</p>
			{/* Keyed by mode: switching drops the other mode's half-made answer. */}
			{w.write === "keys" ? (
				<TypedInput
					key="keys"
					status={status}
					onSubmit={onSubmit}
					onChange={(text) => setAnswer(typedAnswer(text))}
				/>
			) : (
				<SpellTiles key="tiles" {...props} />
			)}
		</div>
	);
}

function SpellTiles({ step, setAnswer, status }: ExerciseViewProps<SpellStep, SpellAnswer>) {
	const t = useStrings();
	// Local rather than lifted: the shell's `answer` is the completed spelling or
	// null, and a half-built word is neither. Remounted per step by key={step.uid}.
	const [slots, setSlots] = useState(() => startSpell(step.letters.length));
	const commit = (next: (string | null)[] | null) => {
		if (!next) return;
		setSlots(next);
		setAnswer(spellAnswer(next));
	};
	const place = (id: string) => {
		if (!status) commit(placeLetter(slots, id));
	};
	const clear = (i: number) => {
		if (!status) commit(clearLetter(slots, i));
	};
	const used = new Set(slots.filter((x): x is string => x !== null));
	// The `!` is safe: every id in `slots` was placed from `step.bank`.
	const glyph = (id: string) => faceText(step.bank.find((o) => o.id === id)!.face);

	return (
		<div>
			{/* One row, slots shrinking to share it: eight 52px slots do not fit a
			    320px phone, and a wrapped row splits the word where it has no break. */}
			<div className="mt-6 flex flex-nowrap items-center gap-1.5">
				{slots.map((id, i) => {
					const right = id !== null && tileLetter(id) === step.letters[i];
					const graded = status === "wrong" && id !== null;
					return (
						<button
							key={i}
							data-testid={`slot-${i}`}
							onClick={() => clear(i)}
							disabled={id === null || !!status}
							aria-label={
								id === null ? t("lesson.spellSlot") : t("lesson.spellRemove", { letter: glyph(id) })
							}
							className={`flex-1 rounded-xl py-2 ${FOCUS}`}
							style={{
								minWidth: 0,
								maxWidth: 52,
								minHeight: 56,
								background: graded
									? right
										? COL.greenSoft
										: COL.redSoft
									: id
										? COL.card
										: COL.well,
								border: `2px ${id ? "solid" : "dashed"} ${
									graded ? (right ? COL.green : COL.red) : id ? COL.line : COL.edge
								}`,
								color: graded ? (right ? COL.greenDark : COL.redDark) : COL.ink,
							}}
						>
							{id !== null && <G size={30}>{glyph(id)}</G>}
						</button>
					);
				})}
			</div>

			<div className="mt-8 flex flex-wrap gap-2">
				{step.bank.map((o) => (
					<button
						key={o.id}
						data-testid={`bank-${o.id}`}
						onClick={() => {
							haptic("tap");
							place(o.id);
						}}
						disabled={used.has(o.id) || !!status}
						className={`rounded-xl px-4 py-2 transition active:translate-y-0.5 ${FOCUS}`}
						style={{
							minHeight: 52,
							background: used.has(o.id) ? COL.well : COL.card,
							border: `2px solid ${used.has(o.id) ? COL.well : COL.line}`,
							boxShadow: used.has(o.id) ? "none" : `0 4px 0 ${COL.edge}`,
							color: used.has(o.id) ? COL.spent : COL.ink,
						}}
					>
						<G size={30}>{faceText(o.face)}</G>
					</button>
				))}
			</div>
		</div>
	);
}

export function SpellFeedback({ step, ok, answer }: FeedbackProps<SpellStep, SpellAnswer>) {
	const course = useCourse();
	const sound = useSound();
	const audio = course.itemById(step.word.id).audio;
	// Spoken once checked, not before: before, it would spell the answer out.
	useEffect(() => {
		if (audio) sound.playAuto(audio);
	}, [audio, sound]);
	return (
		<div className="mt-2">
			<div className="flex items-center gap-3">
				<G size={30}>{step.word.a}</G>
				<div className="text-sm leading-snug" style={{ color: COL.ink }}>
					{step.word.m}
				</div>
				{audio && <SoundButton asset={audio} className="ml-auto" />}
			</div>
			{!ok && isTyped(answer) && <TypedDiff verdict={checkTyped(step.word.a, answer.typed)} />}
		</div>
	);
}

registerView<SpellStep, SpellAnswer>("spell", {
	View: SpellView,
	Feedback: SpellFeedback,
	action: "check",
});
